import { hotkeys } from 'react-keyboard-shortcuts';
import ComponentHint from '../../core/ComponentHint';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import CreateButton from '../../core/CreateButton';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { Component, default as React } from 'react';
import ActionText from '../../core/ActionText';
import ConceptData from '../taxonomies/ConceptData';
import CollectionStructure from './CollectionStructure';
import FindConceptDialog from './FindConceptDialog';
import SearchCollection from './SearchCollection';
import { globalStyles } from '../../core/style/gstheme';
import GetStarted from '../../application-core/GetStarted';

const styles = Object.assign({}, globalStyles);

class CollectionView extends Component {

  constructor(props) {
    super(props);
    this.infoRef = React.createRef();
    this.controlRef = React.createRef();
  }

  hot_keys = {
    'alt+i': {
      priority: 1,
      handler: () => {
        if (this.infoRef && this.infoRef.current) {
          this.infoRef.current.focus();
        }
      }
    },
    'alt+c': {
      priority: 1,
      handler: () => {
        if (this.controlRef && this.controlRef.current) {
          this.controlRef.current.focus();
        }
      }
    }
  };

  render() {

    const { activeConcept, classes, skosXL, data, onDeleteConcept, project, projectUsers,
      showLabelDetail, onSelectViewLanguage, viewLang, ontologyProjects, foundTime, onFindConcept,
      defaultLang, onSelect, onBreakLink, isReadOnly, onIncludeExistingConcept, foundRef,
      appBar, graph, showFloatingLabel, entitlements } = this.props;

    const memberProp = data.type.includes("OrderedCollection") ? "memberList" : "member";
    const maxMembers = memberProp === "memberList" ?
      data[memberProp] && data[memberProp].length >= 1000
      :
      data[memberProp] && data[memberProp].length >= 10000;

    return (
      <div>
        <div className={classes.controlPanel}>
          <div className={classes.views}>
            {appBar}
          </div>
          <div className={classes.controlPanelInner}>
            <div className={classes.controlPanelAdd}>
              {!isReadOnly && data[memberProp] && data[memberProp].length > 0 && !maxMembers &&
                <FindConceptDialog
                  graph={graph}
                  onIncludeConcept={onIncludeExistingConcept}
                  exclude={data[memberProp]} />
              }
              {(isReadOnly || maxMembers) && <div>&nbsp;</div>}
            </div>
            <div className={classes.controlPanelSearch}>
              {data[memberProp] && data[memberProp].length > 0 &&
                <SearchCollection
                  skosXL={skosXL}
                  ordered={memberProp === "memberList"}
                  project={project}
                  graph={graph}
                  collectionIRI={data.id}
                  lang={defaultLang}
                  returnConcept={onFindConcept} />
              }
            </div>
          </div>
          <div tabIndex={0} ref={this.controlRef} data-test="concept-tree" className={classes.tree}>
            {data[memberProp] && data[memberProp].length > 0 ?
              <CollectionStructure
                lang={defaultLang}
                id={data.id}
                defaultLang={defaultLang}
                data={data}
                memberProperty={memberProp}
                foundRef={foundRef}
                foundTime={foundTime}
                graph={graph}
                ordered={memberProp === "memberList"}
                skosXL={skosXL}
                onFindConcept={onFindConcept}
                activeConcept={activeConcept}
                isReadOnly={isReadOnly}
                viewLang={viewLang}
                version={data._version}
                concepts={data[memberProp]}
                onSelect={onSelect}
                onBreakLink={onBreakLink}
              />
              :
              (isReadOnly ?
                <ActionText offset="85px">No concepts.</ActionText>
                :
                <GetStarted text="Add a concept to get started." action={
                  <FindConceptDialog
                    button={<CreateButton label="Add" />}
                    graph={graph}
                    onIncludeConcept={onIncludeExistingConcept}
                    exclude={data[memberProp]} />
                } />
              )
            }
          </div>
        </div>
        <div data-test="view-language" className={classes.viewLanguagePanel}>
          <ProjectLanguageSelector includeLabel={false} onSelect={onSelectViewLanguage} />
        </div>
        <div ref={this.infoRef} tabIndex={0} data-test="info-panel" className={classes.infoPanel}>
          {activeConcept === null ?
            <div>
              <ComponentHint text="collection" />
              <ActionText offset="180px">Select a concept to view the detail.</ActionText>
            </div>
            :
            <ConceptData
              key={activeConcept}
              handleNotAvailable={true}
              id={activeConcept}
              annotate={data.activateAnnotate === true}
              associatedProject={data.associatedProject ?
                Array.isArray(data.associatedProject) ?
                  data.associatedProject
                  :
                  [data.associatedProject]
                :
                []
              }
              entitlements={entitlements}
              graph={graph}
              projectUsers={projectUsers}
              ontologyProjects={ontologyProjects}
              propertyTemplates={data.associatedTemplate}
              hiddenProps={data.hiddenProp}
              showFloatingLabel={showFloatingLabel}
              showLabelDetail={showLabelDetail}
              conceptIRI={activeConcept}
              viewLang={viewLang}
              project={project}
              isReadOnly={true}
              onDeleteConcept={onDeleteConcept} />}
        </div>
      </div>
    );
  }
}

CollectionView.propTypes = {
  classes: PropTypes.object,
  activeConcept: PropTypes.string,
  appBar: PropTypes.object.isRequired,
  entitlements: PropTypes.array.isRequired,
  onIncludeExistingConcept: PropTypes.func.isRequired,
  onDeleteConcept: PropTypes.func.isRequired,
  skosXL: PropTypes.bool.isRequired,
  project: PropTypes.string.isRequired,
  foundTime: PropTypes.number,
  graph: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  projectUsers: PropTypes.array,
  foundRef: PropTypes.object,
  ontologyProjects: PropTypes.array,
  showFloatingLabel: PropTypes.func.isRequired,
  showLabelDetail: PropTypes.func.isRequired,
  onSelectViewLanguage: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onFindConcept: PropTypes.func.isRequired,
  viewLang: PropTypes.string,
  onBreakLink: PropTypes.func.isRequired,
  defaultLang: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired
};

export default withStyles(styles)(hotkeys(CollectionView));